/**
 * Phase 13 (Session 1) — Admin V5 Learning Plans V2 panel.
 *
 * Consolidated single-master workflow for managing a student's course:
 * one plan → many modules → many sessions + one shared classroom link +
 * one instalment schedule. Fully additive; existing tabs untouched.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, RefreshCcw, Plus, Copy, ChevronDown, ChevronRight, X,
  Search, GraduationCap, DollarSign, Calendar, Users, Layers,
  Link as LinkIcon, Trash2, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const WEEKDAYS = [
  { key: 'Mon', label: 'Mon' }, { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' }, { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' }, { key: 'Sat', label: 'Sat' },
  { key: 'Sun', label: 'Sun' },
];

const PAYMENT_OPTIONS = [
  { value: 'full_payment', label: 'Full Payment' },
  { value: 'deposit_balance', label: 'Deposit + Balance' },
  { value: 'weekly', label: 'Weekly Instalments' },
  { value: 'fortnightly', label: 'Fortnightly Instalments' },
  { value: 'monthly', label: 'Monthly Instalments' },
  { value: 'custom', label: 'Custom Instalment Plan' },
];

const ACCESS_POLICIES = [
  { value: 'full_before_access', label: 'Full payment required before access' },
  { value: 'deposit_before_access', label: 'Deposit required before access' },
  { value: 'instalments_allowed', label: 'Instalments allowed while course active' },
  { value: 'admin_approved', label: 'Admin-approved access' },
  { value: 'waived', label: 'Payment waived' },
];

const AdminLearningPlansV2Panel = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/admin/learning-plans-v2');
      setPlans(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load plans');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleExpand = async (planId) => {
    if (expandedId === planId) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(planId);
    setDetailLoading(true);
    try {
      const r = await ax.get(`/api/admin/learning-plans-v2/${planId}`);
      setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setDetailLoading(false); }
  };

  const copyLink = (url, label) => {
    if (!url) { toast.error(`No ${label} link`); return; }
    const full = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(full).then(
      () => toast.success(`${label} link copied`),
      () => toast.error('Copy failed'),
    );
  };

  const markInstalmentPaid = async (planId, instalmentId, ref) => {
    try {
      const r = await ax.patch(`/api/admin/learning-plans-v2/${planId}/instalments/${instalmentId}`,
        { status: 'paid', payment_reference: ref || 'MANUAL' });
      // Refresh detail + card
      setDetail((prev) => (prev ? {
        ...prev,
        plan: { ...prev.plan, ...(r.data?.plan_stats || {}) },
        instalments: prev.instalments.map((i) => (i.instalment_id === instalmentId ? r.data.instalment : i)),
      } : prev));
      setPlans((prev) => prev.map((p) => (p.learning_plan_id === planId ? { ...p, ...(r.data?.plan_stats || {}) } : p)));
      toast.success('Instalment marked paid');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const cancelPlan = async (planId) => {
    if (!window.confirm('Cancel this Learning Plan? Scheduled sessions & pending instalments will be cancelled. Paid records are preserved.')) return;
    try {
      const r = await ax.delete(`/api/admin/learning-plans-v2/${planId}`);
      toast.success(`Plan cancelled (${r.data?.sessions_cancelled || 0} sessions, ${r.data?.instalments_cancelled || 0} instalments)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  return (
    <Card data-testid="admin-lp2-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Learning Plans V2</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="lp2-refresh-btn">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="lp2-create-btn">
            <Plus className="h-4 w-4 mr-1" /> New Learning Plan
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500 -mt-2">
          Single source of truth: one plan groups modules, sessions, instalments, tutor earnings, attendance and the shared classroom.
          Legacy “Bookings &amp; Series”, “Course Bookings” and the older Learning Plans tab remain available unchanged.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : plans.length === 0 ? (
          <div className="text-sm italic text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
            No learning plans yet. Click <strong>New Learning Plan</strong> to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {plans.map((p) => (
              <PlanCard
                key={p.learning_plan_id}
                plan={p}
                expanded={expandedId === p.learning_plan_id}
                detail={expandedId === p.learning_plan_id ? detail : null}
                loadingDetail={expandedId === p.learning_plan_id && detailLoading}
                onToggle={() => toggleExpand(p.learning_plan_id)}
                onCopyLink={copyLink}
                onCancel={() => cancelPlan(p.learning_plan_id)}
                onMarkPaid={(iid, ref) => markInstalmentPaid(p.learning_plan_id, iid, ref)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {createOpen && (
        <CreatePlanModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchAll(); }} />
      )}
    </Card>
  );
};

// ============================================================================
// Plan card
// ============================================================================

const PlanCard = ({ plan, expanded, detail, loadingDetail, onToggle, onCopyLink, onCancel, onMarkPaid }) => {
  const cancelled = plan.overall_status === 'cancelled';
  const cur = plan.currency || 'AUD';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`lp2-card-${plan.learning_plan_id}`}>
      <div className="p-4 bg-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={onToggle} className="text-gray-500 hover:text-black" data-testid={`lp2-toggle-${plan.learning_plan_id}`}>
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <div className="text-base font-bold text-gray-900">{plan.course_name}</div>
              {plan.programme_type && <Badge variant="outline" className="text-[10px]">{plan.programme_type}</Badge>}
              {cancelled && <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Cancelled</Badge>}
              <Badge className={`text-[10px] ${plan.payment_status === 'paid_in_full' ? 'bg-green-600' : plan.payment_status === 'partially_paid' ? 'bg-amber-500' : 'bg-gray-500'}`}>
                {(plan.payment_status || 'not_started').replaceAll('_', ' ')}
              </Badge>
            </div>
            <div className="mt-1 text-xs text-gray-600 space-y-0.5">
              <div><GraduationCap className="h-3 w-3 inline mr-1 text-gray-400" /> {plan.student_name} · <span className="text-gray-500">{plan.student_email}</span></div>
              <div><Calendar className="h-3 w-3 inline mr-1 text-gray-400" /> {plan.course_start_date} → {plan.course_end_date}</div>
              <div><Layers className="h-3 w-3 inline mr-1 text-gray-400" /> {(plan.modules || []).length} module{(plan.modules || []).length === 1 ? '' : 's'} · {plan.sessions_total ?? 0} sessions total</div>
            </div>
          </div>

          <div className="text-right shrink-0 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Package total</div>
            <div className="text-2xl font-black" data-testid={`lp2-total-${plan.learning_plan_id}`}>{cur} {plan.total_price}</div>
            <div className="text-xs text-gray-500 mt-0.5">Paid <span className="text-green-700 font-bold" data-testid={`lp2-paid-${plan.learning_plan_id}`}>{cur} {plan.amount_paid ?? 0}</span></div>
            <div className="text-xs text-gray-500">Balance <span className="text-red-700 font-bold" data-testid={`lp2-balance-${plan.learning_plan_id}`}>{cur} {plan.balance_remaining ?? plan.total_price}</span></div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <Stat label="Progress" value={`${plan.progress_percent ?? 0}%`} />
          <Stat label="Attendance" value={`${plan.attendance_percent ?? 0}%`} />
          <Stat label="Projected earnings" value={`${cur} ${plan.projected_tutor_earnings ?? 0}`} />
          <Stat label="Confirmed earnings" value={`${cur} ${plan.confirmed_tutor_earnings ?? 0}`} accent="green" />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onCopyLink(plan.student_join_url, 'Student')}
            data-testid={`lp2-copy-student-${plan.learning_plan_id}`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy student link
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCopyLink(plan.tutor_join_url, 'Tutor')}
            data-testid={`lp2-copy-tutor-${plan.learning_plan_id}`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy tutor link
          </Button>
          {!cancelled && (
            <Button size="sm" variant="outline" onClick={onCancel}
              className="text-red-700 border-red-200 hover:bg-red-50"
              data-testid={`lp2-cancel-${plan.learning_plan_id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancel plan
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50/70 p-3">
          {loadingDetail || !detail ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-3"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading detail…</div>
          ) : (
            <PlanDetail plan={detail.plan} sessions={detail.sessions} instalments={detail.instalments} onMarkPaid={onMarkPaid} />
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-lg border border-gray-200 bg-white p-2 ${accent === 'green' ? 'border-green-200' : ''}`}>
    <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className={`text-sm font-bold ${accent === 'green' ? 'text-green-700' : 'text-gray-900'}`}>{value}</div>
  </div>
);

const PlanDetail = ({ plan, sessions, instalments, onMarkPaid }) => {
  const cur = plan.currency || 'AUD';
  return (
    <div className="space-y-4">
      {/* Modules */}
      <Section title={`Modules (${(plan.modules || []).length})`} testId="lp2-detail-modules">
        <div className="grid sm:grid-cols-2 gap-2">
          {(plan.modules || []).map((m) => (
            <div key={m.module_id} className="rounded-lg border border-gray-200 bg-white p-2.5 text-xs" data-testid={`lp2-module-${m.module_id}`}>
              <div className="font-bold text-gray-900">{m.module_name}</div>
              <div className="text-gray-600">
                {(m.weekday_labels || []).join(', ') || '—'} · {m.start_time} · {m.duration_minutes}min · {m.planned_session_count} sessions
              </div>
              <div className="text-gray-500 mt-0.5">
                Tutor: {m.assigned_tutor_name || m.assigned_tutor_email || '—'} · {m.hourly_tutor_rate_currency} {m.hourly_tutor_rate}/hr
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Instalments */}
      <Section title={`Payment schedule (${instalments.length})`} testId="lp2-detail-instalments">
        <div className="space-y-1.5">
          {instalments.map((i) => (
            <div key={i.instalment_id}
              className={`flex items-center justify-between text-xs rounded-lg border p-2 ${i.status === 'paid' ? 'bg-green-50 border-green-200' : i.status === 'cancelled' ? 'bg-gray-100 border-gray-200 text-gray-400' : 'bg-white border-gray-200'}`}
              data-testid={`lp2-instalment-${i.instalment_id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`text-[10px] uppercase tracking-wider font-bold w-16 ${i.status === 'paid' ? 'text-green-700' : 'text-gray-500'}`}>
                  {i.kind === 'deposit' ? 'Deposit' : `#${i.instalment_number}`}
                </div>
                <div className="font-bold text-gray-900">{i.currency} {i.amount.toFixed ? i.amount.toFixed(2) : i.amount}</div>
                <div className="text-gray-500">Due {i.due_date}</div>
                {i.payment_reference && <div className="text-gray-400">· ref {i.payment_reference}</div>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[10px] ${i.status === 'paid' ? 'border-green-300 text-green-700' : 'border-gray-300 text-gray-600'}`}>
                  {i.status}
                </Badge>
                {i.status === 'pending' && (
                  <Button size="sm" variant="outline" className="text-[10px] h-6"
                    onClick={() => {
                      const ref = window.prompt('Payment reference (optional)', '');
                      onMarkPaid(i.instalment_id, ref);
                    }}
                    data-testid={`lp2-mark-paid-${i.instalment_id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Mark paid
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Sessions summary (compact list — full attendance edits live in Course Bookings for now) */}
      <Section title={`Sessions (${sessions.length})`} testId="lp2-detail-sessions">
        <div className="max-h-64 overflow-y-auto space-y-1">
          {sessions.slice(0, 40).map((s) => (
            <div key={s.session_id} className="flex items-center justify-between text-[11px] rounded border border-gray-200 bg-white p-1.5" data-testid={`lp2-session-${s.session_id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-gray-500">#{s.session_number}</span>
                <span className="font-bold">{s.module_name}</span>
                <span className="text-gray-500">{s.day_of_week} {s.scheduled_date} {(s.scheduled_start || '').slice(11, 16)}</span>
              </div>
              <Badge variant="outline" className={`text-[9px] ${s.status === 'completed' ? 'border-green-300 text-green-700' : s.status === 'cancelled' ? 'border-red-300 text-red-700' : 'border-gray-300 text-gray-600'}`}>
                {s.status}
              </Badge>
            </div>
          ))}
          {sessions.length > 40 && (
            <div className="text-[10px] text-gray-500 italic p-2">…and {sessions.length - 40} more sessions.</div>
          )}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, testId, children }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">{title}</div>
    {children}
  </div>
);

// ============================================================================
// CreatePlanModal — wizard
// ============================================================================

const emptyModule = () => ({
  module_name: '', assigned_tutor_email: '', assigned_tutor_name: '',
  weekdays: [], start_time: '17:00', duration_minutes: 60,
  planned_session_count: 4, hourly_tutor_rate: 40, hourly_tutor_rate_currency: 'USD',
});

const CreatePlanModal = ({ onClose, onCreated }) => {
  const today = new Date().toISOString().slice(0, 10);
  const in60 = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    student_email: '', student_name: '', student_id: '', parent_email: '',
    course_name: '', programme_type: '',
    course_start_date: today, course_end_date: in60,
    timezone: 'Australia/Melbourne',
    total_price: 0, currency: 'AUD',
    payment_access_policy: 'instalments_allowed',
    modules: [emptyModule()],
    instalment_plan: {
      payment_option: 'full_payment',
      deposit_amount: 0, number_of_instalments: 0,
      first_due_date: today, grace_period_days: 3,
    },
    notes: '',
  });
  const [manualStudent, setManualStudent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setInst = (k, v) => setForm((p) => ({ ...p, instalment_plan: { ...p.instalment_plan, [k]: v } }));

  const setModule = (idx, patch) => setForm((p) => ({
    ...p,
    modules: p.modules.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
  }));
  const addModule = () => setForm((p) => ({ ...p, modules: [...p.modules, emptyModule()] }));
  const removeModule = (idx) => setForm((p) => ({ ...p, modules: p.modules.filter((_, i) => i !== idx) }));

  const toggleWeekday = (idx, key) => setForm((p) => ({
    ...p,
    modules: p.modules.map((m, i) => (i === idx ? {
      ...m,
      weekdays: m.weekdays.includes(key) ? m.weekdays.filter((w) => w !== key) : [...m.weekdays, key],
    } : m)),
  }));

  const runPreview = async () => {
    try {
      const r = await ax.post('/api/admin/learning-plans-v2/preview', form);
      setPreview(r.data);
      toast.success(`Preview: ${r.data.total_sessions} sessions, ${r.data.instalments.length} instalments totalling ${r.data.currency} ${r.data.instalments.reduce((s, i) => s + i.amount, 0).toFixed(2)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const submit = async () => {
    if (!form.student_email || !form.course_name || !form.total_price) {
      toast.error('Provide student, course name and total price.'); return;
    }
    if (!form.modules.length) { toast.error('Add at least one module.'); return; }
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/learning-plans-v2', form);
      toast.success(`Plan created · ${r.data?.sessions_generated} sessions · ${r.data?.instalments_generated} instalments`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="lp2-create-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Create Learning Plan</h3>
            <p className="text-xs text-gray-500 mt-1">One plan · one package price · many modules · one classroom link · one instalment schedule.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black"><X className="h-4 w-4" /></button>
        </div>

        {/* Student */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Student *</Label>
            <button className="text-[10px] text-red-700 hover:underline" onClick={() => setManualStudent((m) => !m)} data-testid="lp2-toggle-manual-student">
              {manualStudent ? 'Use registered list' : 'Add manually'}
            </button>
          </div>
          {manualStudent ? (
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Student email" value={form.student_email} onChange={(e) => set('student_email', e.target.value)} data-testid="lp2-student-email-manual" />
              <Input placeholder="Student full name" value={form.student_name} onChange={(e) => set('student_name', e.target.value)} data-testid="lp2-student-name-manual" />
            </div>
          ) : (
            <UserSearchSelect
              endpoint="/api/admin/lookup/students"
              placeholder="Search students by name or email…"
              selectedEmail={form.student_email}
              onSelect={(u) => setForm((p) => ({ ...p, student_email: u.email, student_name: u.name, student_id: u.id || '' }))}
              testId="lp2-student-search"
            />
          )}
        </div>

        {/* Course top-level */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Course / Package name *">
            <Input value={form.course_name} onChange={(e) => set('course_name', e.target.value)} placeholder="e.g. IELTS Intensive Programme" data-testid="lp2-course-name" />
          </Field>
          <Field label="Programme type">
            <Input value={form.programme_type} onChange={(e) => set('programme_type', e.target.value)} placeholder="e.g. IELTS Intensive" data-testid="lp2-programme-type" />
          </Field>
          <Field label="Course start date *"><Input type="date" value={form.course_start_date} onChange={(e) => set('course_start_date', e.target.value)} data-testid="lp2-start" /></Field>
          <Field label="Course end date *"><Input type="date" value={form.course_end_date} onChange={(e) => set('course_end_date', e.target.value)} data-testid="lp2-end" /></Field>
          <Field label="Total package price *">
            <div className="flex gap-1">
              <Input type="number" value={form.total_price} onChange={(e) => set('total_price', parseFloat(e.target.value) || 0)} data-testid="lp2-total-price" />
              <select value={form.currency} onChange={(e) => set('currency', e.target.value)} className="h-9 px-2 rounded border border-gray-300 text-sm">
                {['AUD', 'USD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Payment access policy">
            <select value={form.payment_access_policy} onChange={(e) => set('payment_access_policy', e.target.value)}
              className="w-full h-9 px-2 rounded border border-gray-300 text-sm" data-testid="lp2-access-policy">
              {ACCESS_POLICIES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
        </div>

        {/* Modules */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Modules ({form.modules.length})</Label>
            <Button size="sm" variant="outline" onClick={addModule} data-testid="lp2-add-module">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add module
            </Button>
          </div>
          <p className="text-[10px] text-gray-500 mb-2">Instructional components — no separate charges. Payment lives at plan level.</p>
          <div className="space-y-2">
            {form.modules.map((m, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60" data-testid={`lp2-module-row-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-gray-900">Module {idx + 1}</div>
                  {form.modules.length > 1 && (
                    <button className="text-[10px] text-red-700 hover:underline" onClick={() => removeModule(idx)}>Remove</button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Field label="Module name *">
                    <Input value={m.module_name} onChange={(e) => setModule(idx, { module_name: e.target.value })} placeholder="e.g. Listening" data-testid={`lp2-module-name-${idx}`} />
                  </Field>
                  <Field label="Tutor">
                    <UserSearchSelect
                      endpoint="/api/admin/lookup/tutors"
                      placeholder="Search tutors…"
                      selectedEmail={m.assigned_tutor_email}
                      onSelect={(u) => setModule(idx, { assigned_tutor_email: u.email, assigned_tutor_name: u.name })}
                      testId={`lp2-module-tutor-${idx}`}
                      secondaryText={(u) => u.subjects || u.email}
                    />
                  </Field>
                  <Field label="Start time"><Input type="time" value={m.start_time} onChange={(e) => setModule(idx, { start_time: e.target.value })} /></Field>
                  <Field label="Duration (min)"><Input type="number" value={m.duration_minutes} onChange={(e) => setModule(idx, { duration_minutes: parseInt(e.target.value, 10) || 60 })} /></Field>
                  <Field label="Planned sessions"><Input type="number" value={m.planned_session_count} onChange={(e) => setModule(idx, { planned_session_count: parseInt(e.target.value, 10) || 0 })} data-testid={`lp2-module-count-${idx}`} /></Field>
                  <Field label="Hourly tutor rate">
                    <div className="flex gap-1">
                      <Input type="number" value={m.hourly_tutor_rate} onChange={(e) => setModule(idx, { hourly_tutor_rate: parseFloat(e.target.value) || 0 })} />
                      <select value={m.hourly_tutor_rate_currency} onChange={(e) => setModule(idx, { hourly_tutor_rate_currency: e.target.value })}
                        className="h-9 px-2 rounded border border-gray-300 text-sm">
                        {['USD', 'AUD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </Field>
                </div>
                <div className="mt-2">
                  <Label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Weekdays</Label>
                  <div className="flex flex-wrap gap-1">
                    {WEEKDAYS.map((w) => (
                      <button key={w.key} type="button" onClick={() => toggleWeekday(idx, w.key)}
                        aria-pressed={m.weekdays.includes(w.key)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                          m.weekdays.includes(w.key) ? 'bg-red-700 border-red-700 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`lp2-module-${idx}-day-${w.key}`}>
                        {w.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instalment plan */}
        <div className="mt-5">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1.5">Payment plan</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Option">
              <select value={form.instalment_plan.payment_option} onChange={(e) => setInst('payment_option', e.target.value)}
                className="w-full h-9 px-2 rounded border border-gray-300 text-sm" data-testid="lp2-payment-option">
                {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="First due date"><Input type="date" value={form.instalment_plan.first_due_date} onChange={(e) => setInst('first_due_date', e.target.value)} data-testid="lp2-first-due" /></Field>
            {form.instalment_plan.payment_option !== 'full_payment' && (
              <>
                <Field label="Deposit amount"><Input type="number" value={form.instalment_plan.deposit_amount} onChange={(e) => setInst('deposit_amount', parseFloat(e.target.value) || 0)} data-testid="lp2-deposit" /></Field>
                {['weekly', 'fortnightly', 'monthly'].includes(form.instalment_plan.payment_option) && (
                  <Field label="Number of instalments"><Input type="number" value={form.instalment_plan.number_of_instalments} onChange={(e) => setInst('number_of_instalments', parseInt(e.target.value, 10) || 0)} data-testid="lp2-num-instalments" /></Field>
                )}
                <Field label="Grace period (days)"><Input type="number" value={form.instalment_plan.grace_period_days} onChange={(e) => setInst('grace_period_days', parseInt(e.target.value, 10) || 0)} /></Field>
              </>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-between gap-2 flex-wrap">
          <Button variant="outline" onClick={runPreview} data-testid="lp2-preview-btn">
            <Search className="h-4 w-4 mr-1" /> Preview schedule
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="lp2-create-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="lp2-create-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LinkIcon className="h-4 w-4 mr-1" />}
              Create plan
            </Button>
          </div>
        </div>

        {preview && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Preview</div>
            <div className="text-xs text-gray-700">
              <div>Total sessions across modules: <strong>{preview.total_sessions}</strong></div>
              <div className="mt-1">Instalment schedule ({preview.instalments.length} rows totalling {preview.currency} {preview.instalments.reduce((s, i) => s + i.amount, 0).toFixed(2)}):</div>
              <ul className="mt-1 space-y-0.5 text-[11px] ml-4 list-disc">
                {preview.instalments.map((i, k) => (
                  <li key={k}>{i.kind} · {i.currency} {i.amount.toFixed ? i.amount.toFixed(2) : i.amount} · due {i.due_date}</li>
                ))}
              </ul>
              <div className="mt-2 text-[10px] text-gray-500">Preview does not save anything. Click Create plan to persist.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// UserSearchSelect (shared with Phase 12 pattern)
// ============================================================================

const UserSearchSelect = ({ endpoint, placeholder, selectedEmail, onSelect, testId, secondaryText }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosenLabel, setChosenLabel] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await ax.get(endpoint, { params: { q, limit: 15 } }); setItems(r.data?.items || []); }
      catch { setItems([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, endpoint]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input
          value={selectedEmail && !open ? (chosenLabel || selectedEmail) : q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={placeholder}
          className="pl-9"
          data-testid={testId}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-2 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</div>
          ) : items.length === 0 ? (
            <div className="p-2 text-xs italic text-gray-400">No matches.</div>
          ) : (
            items.map((u) => (
              <button key={u.id || u.email} type="button"
                onClick={() => { onSelect(u); setChosenLabel(`${u.name} — ${u.email}`); setOpen(false); setQ(''); }}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${u.email === selectedEmail ? 'bg-red-50/50' : ''}`}
                data-testid={`${testId}-item-${u.email}`}>
                <div className="text-sm font-medium text-gray-900">{u.name}</div>
                <div className="text-[11px] text-gray-500">{u.email}{secondaryText ? ` · ${secondaryText(u)}` : ''}</div>
              </button>
            ))
          )}
        </div>
      )}
      {selectedEmail && (
        <div className="mt-1 text-[10px] text-gray-500">
          Selected: <span className="font-mono text-gray-700" data-testid={`${testId}-selected-email`}>{selectedEmail}</span>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</span>
    {children}
  </label>
);

export default AdminLearningPlansV2Panel;
